/**
 * A-16 — one order, as the admin tracks it (Orders and Transactions open it in a dialog): where it
 * stands, who it is for and where it goes, what was dispensed and charged (with GST), its payment
 * attempts and refunds, and each status change, oldest first.
 */

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { getAdminOrder } from "../../api/adminOrders";
import type { AdminOrderDetail as Detail } from "../../api/adminOrders";
import { describeError } from "../../api/errors";
import { formatINR } from "../../lib/money";
import { phoneLabel } from "../../lib/phone";

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const PAYMENT_TONE: Record<string, string> = {
  succeeded: "bg-emerald-50 text-emerald-700",
  processed: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  initiated: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  expired: "bg-gray-100 text-gray-600",
  cancelled: "bg-gray-100 text-gray-600",
};

export function StatusPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${PAYMENT_TONE[status] ?? "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

export function AdminOrderDetail({ orderId }: { orderId: string }) {
  const { data: order, error, isLoading } = useQuery({ queryKey: ["admin-order", orderId], queryFn: () => getAdminOrder(orderId) });
  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (error || !order) return <p className="text-sm text-red-600">{describeError(error)}</p>;
  return <OrderView order={order} />;
}

function OrderView({ order: o }: { order: Detail }) {
  const priced = !!o.pricing_published_at;
  const address = o.delivery_address as Record<string, string | null | undefined>;
  const addressLines = [
    address.name,
    [address.line1, address.line2, address.landmark].filter(Boolean).join(", "),
    [address.city, address.state_code, address.pin_code].filter(Boolean).join(" "),
    address.phone ? phoneLabel(String(address.phone)) : null,
  ].filter(Boolean);
  const refunded = Number(o.refunded_amount) > 0;

  return (
    <div className="space-y-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-base font-semibold text-gray-800">{o.order_id}</span>
        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand">{o.status_label}</span>
        {o.cancelled_at && o.status_code !== "cancelled" && (
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Cancelled</span>
        )}
        {refunded && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Refunded {formatINR(o.refunded_amount)}</span>}
      </div>

      <dl className="grid gap-x-6 gap-y-3 rounded-lg border bg-gray-50 p-3 sm:grid-cols-2">
        <Fact label="Patient">
          {o.patient_name ?? "—"}
          {o.patient_phone && <span className="text-gray-500"> · {phoneLabel(o.patient_phone)}</span>}
          {o.for_family_member_name && <span className="block text-xs text-gray-500">For {o.for_family_member_name}</span>}
        </Fact>
        <Fact label="Deliver to">
          {addressLines.length ? addressLines.map((line, i) => <span key={i} className="block">{line}</span>) : "—"}
        </Fact>
        <Fact label="Placed">{when(o.created_at)}</Fact>
        <Fact label="Pharmacist">{o.assignee_name ?? "Not claimed"}</Fact>
        <Fact label="Paid">{o.paid_at ? when(o.paid_at) : "Not paid"}</Fact>
        {o.cancelled_at && <Fact label="Cancelled">{when(o.cancelled_at)}</Fact>}
        {o.placement_note && <Fact label="Patient's note">“{o.placement_note}”</Fact>}
      </dl>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Items</h4>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[34rem] text-left text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">Medicine</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">MRP</th>
                <th className="px-3 py-2 text-right font-medium">Taxable</th>
                <th className="px-3 py-2 text-right font-medium">GST</th>
              </tr>
            </thead>
            <tbody>
              {o.lines.map((ln, i) => {
                const gst = Number(ln.cgst_amount) + Number(ln.sgst_amount) + Number(ln.igst_amount);
                return (
                  <tr key={i} className={`border-t ${ln.is_available ? "" : "text-gray-400"}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{ln.medicine_name}</div>
                      <div className="text-gray-400">
                        HSN {ln.hsn_code || "—"}
                        {!ln.is_available && " · not available"}
                        {ln.quantity_remark && ` · ${ln.quantity_remark}`}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {ln.quantity_dispensed}
                      <span className="text-gray-400">/{ln.quantity_prescribed}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{priced ? formatINR(ln.mrp_per_unit) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{priced ? formatINR(ln.taxable_value) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {priced ? (
                        <>
                          {formatINR(gst)}
                          <span className="block text-gray-400">{Number(ln.gst_pct)}%</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {priced ? (
          <dl className="ml-auto mt-2 w-full max-w-xs space-y-1 text-sm">
            <Total label="Medicines" value={o.medicines_subtotal} />
            <Total label="Delivery" value={o.delivery_charge} />
            <Total label="GST" value={o.gst_total} />
            <Total label="Total" value={o.final_amount} strong />
            {refunded && <Total label="Refunded" value={`-${o.refunded_amount}`} />}
          </dl>
        ) : (
          <p className="mt-2 text-xs text-gray-500">The pharmacy hasn't published the price yet.</p>
        )}
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Payments</h4>
        {o.payments.length === 0 ? (
          <p className="text-xs text-gray-500">No payment attempts.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {o.payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusPill status={p.status} />
                    <span className="tabular-nums">{formatINR(p.amount)}</span>
                  </div>
                  <div className="truncate text-xs text-gray-400">
                    {when(p.completed_at ?? p.initiated_at)} · {p.rzp_payment_id ?? p.rzp_order_id}
                  </div>
                  {p.failure_reason && <div className="text-xs text-red-600">{p.failure_reason}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {o.refunds.length > 0 && (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-gray-700">Refunds</h4>
          <ul className="divide-y rounded-lg border">
            {o.refunds.map((r) => (
              <li key={r.id} className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <StatusPill status={r.status} />
                  <span className="tabular-nums">{formatINR(r.refunded_amount ?? r.requested_amount)}</span>
                </div>
                <div className="truncate text-xs text-gray-400">
                  {when(r.processed_at ?? r.initiated_at)}
                  {r.rzp_refund_id && ` · ${r.rzp_refund_id}`}
                </div>
                <div className="text-xs text-gray-600">{r.reason}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Status history</h4>
        <ol className="space-y-2 border-l pl-4">
          {o.history.map((e, i) => (
            <li key={i} className="relative">
              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-brand" aria-hidden />
              <div className="font-medium text-gray-800">{e.to_label}</div>
              <div className="text-xs text-gray-400">
                {when(e.at)} · {e.actor_name ?? e.actor_role}
              </div>
              {e.remark && <div className="text-xs text-gray-600">{e.remark}</div>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-gray-800">{children}</dd>
    </div>
  );
}

function Total({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-4 ${strong ? "border-t pt-1 font-semibold text-gray-800" : "text-gray-600"}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{formatINR(value)}</dd>
    </div>
  );
}
